import _ from 'lodash'
import Vuex, {StoreOptions, MutationPayload} from 'vuex'
import { RootState } from '@/store/types';

/**
 * Listener
 *
 * Función que puede ser asociada a una mutación de Vuex, cuando dicha mutación
 * sea ejecutada, se invocará al listener.
 */
export type Listener<S> = (mutation: MutationPayload, state: S) => void

/**
 * Store de Vuex
 *
 * Modificación del Store que añade la capacidad de registrar event listeners
 * solo a determinadas mutaciones.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export default class Store<S> extends Vuex.Store<S> {

    private subscriptions = new Map<string, Array<Listener<S>>>()

    /**
     * Constructor
     *
     * @param {StoreOptions<S>} options
     */
    constructor (options: StoreOptions<S>) {
        super(options)

        // Subscripción que permite despachar listeners en base a una mutación
        this.subscribe((mutation, state) => {
            const subscription = this.subscriptions.get(mutation.type)
            if (!subscription) {
                return
            }

            subscription.forEach(fn => fn(mutation, state))
        })
    }

    /**
     * Permite escuchar una o varias mutaciones y ejecutar una función de callback
     *
     * @param {string[]} mutation
     * @param {Function} listener
     */
    public listen (mutation: string[] | string, listener: Listener<S>): this {
        if (Array.isArray(mutation)) {
            mutation.forEach(m => this.addSubscription(m, listener))

            return this
        }

        this.addSubscription(mutation, listener)

        return this
    }

    /**
     * Añade una subscripción
     *
     * @param {string} mutation
     * @param {Function} listener
     * @returns {this<S>}
     */
    private addSubscription (mutation: string, listener: Listener<S>): this {
        const subscription = this.subscriptions.get(mutation) || []

        // Añadir nuevo listener a la subscripción
        if (subscription.length > 0) {
            subscription.push(listener)

            return this
        }

        this.subscriptions.set(mutation, [listener])

        return this
    }

    /**
     * Retorna una propiedad del estado
     *
     * @param path Ruta del estado
     * @param _default Valor por defecto si no existe
     */
    public get (path: string, _default: any = null) {
        return _.get(this.state, path, _default)
    }
}
